import { Box, Button, Divider, Flex, Group, IconFont, Input, Text, Tooltip } from '@byecode/ui'
import type { FlowNode, LLMActionConfig, NodeTypes } from '@lighthouse/shared'
import {
    AINodeType,
    getAllCanBeUpstreamNodes,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    NodeFieldPreview,
    useAtomData,
    VariableSelect,
    VisualInputType
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import OutputVariable from '@/containers/FlowDetail/components/OutputVariable'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import SelectModel from './SelectModel'
import * as SC from './styles'

type Props = {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    value: LLMActionConfig
    onChange: (value: LLMActionConfig) => void
}

const supportVisualInputModels = new Set(['QWEN_VL_MAX', 'QWEN_VL_PLUS', 'QWEN_VL_OCR'])

export const LLMActionConfigure: React.FC<Props> = ({ allParentNodes, value, onChange }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { type } = useFlowContainerContext()
    const isInFlow = !!type

    const model = value.model

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                      allUpstreamNodes: allParentNodes,
                      dataSourceList
                  })
                : undefined,
        [allParentNodes, dataSourceList, isInFlow, workflowList]
    )

    const userOption = useGetUserRecordOptions()

    const disabledVisualUnderstandingInput = useMemo(() => {
        return !supportVisualInputModels.has(model)
    }, [model])

    return (
        <SC.Container>
            <Group label="模型">
                <Box px={8} py={4}>
                    <SelectModel value={value.model} onChange={model => onChange({ ...value, model })} />
                </Box>
            </Group>
            <Divider />
            {(() => {
                const list: LLMActionConfig['inputVariables'] = value.inputVariables || []
                return (
                    <Group
                        label={
                            <PopoverQuestion
                                type="visualUnderstandingInput"
                                renderTarget={(node, ref) => (
                                    <Flex alignItems="center" gap={4} ref={ref}>
                                        视觉理解输入 {node}{' '}
                                    </Flex>
                                )}
                            />
                        }
                        extra={[
                            <Tooltip key={1} title="所选模型不支持视觉理解" disabled={!disabledVisualUnderstandingInput}>
                                <Button
                                    type="text"
                                    size="sm"
                                    disabled={disabledVisualUnderstandingInput}
                                    style={{ padding: '0 4px' }}
                                    onClick={e => {
                                        const item = {
                                            id: nanoid(),
                                            name: '未命名变量',
                                            type: VisualInputType.IMAGE,
                                            isEdit: false,
                                            value: null as unknown as LLMActionConfig['inputVariables'][number]['value']
                                        }
                                        const newList = [...list, item]
                                        onChange({ ...value, inputVariables: newList })
                                        e.stopPropagation()
                                    }}
                                >
                                    <IconFont type="Add2" color="var(--color-gray-400)" size={20} />
                                </Button>
                            </Tooltip>
                        ]}
                    >
                        {list.length > 0 && (
                            <Flex pt={8} gap={12} direction="column" pb={4} px={8}>
                                {list.map((item, index) => (
                                    <Flex key={item.id} gap={8} direction="column">
                                        <Flex alignItems="center">
                                            <Flex alignItems="center" gap={6} style={{ flex: 1, height: 24 }}>
                                                {item.isEdit ? (
                                                    <Input
                                                        autoSelect
                                                        size="xs"
                                                        placeholder="变量名"
                                                        defaultValue={item.name}
                                                        onBlur={e => {
                                                            const nextName = e.target.value
                                                            const newList = [...list]
                                                            newList.splice(index, 1, {
                                                                ...item,
                                                                name: nextName || '未命名变量',
                                                                isEdit: false
                                                            })
                                                            onChange({ ...value, inputVariables: newList })
                                                        }}
                                                    />
                                                ) : (
                                                    <Text size={14}>{item.name}</Text>
                                                )}
                                                {!item.isEdit && (
                                                    <SC.Icon
                                                        type="edit"
                                                        size={12}
                                                        onClick={() => {
                                                            const newList = [...list]
                                                            newList.splice(index, 1, {
                                                                ...item,
                                                                isEdit: !item.isEdit
                                                            })
                                                            onChange({ ...value, inputVariables: newList })
                                                        }}
                                                    />
                                                )}
                                            </Flex>
                                            <SC.Icon
                                                type="Trash"
                                                size={16}
                                                onClick={() => {
                                                    const newList = [...list]
                                                    newList.splice(index, 1)
                                                    onChange({ ...value, inputVariables: newList })
                                                }}
                                            />
                                        </Flex>
                                        <VariableSelect
                                            withinPortal
                                            upstreamOptions={upstreamOptions}
                                            value={item.value}
                                            innerTypes={getInputInnerTypeMatch('TEXT')}
                                            width="100%"
                                            disablePexels
                                            userOption={userOption}
                                            onChange={newValue => {
                                                const newList = [...list]
                                                newList.splice(index, 1, {
                                                    ...item,
                                                    value: newValue
                                                })
                                                onChange({ ...value, inputVariables: newList })
                                            }}
                                        />
                                    </Flex>
                                ))}
                            </Flex>
                        )}
                    </Group>
                )
            })()}
            <Divider />
            <Group label="提示词">
                <Box px={8}>
                    <SC.Item>
                        <SC.Text>
                            系统提示词
                            <SC.Text color="red">*</SC.Text>
                            <Tooltip
                                title={
                                    <SC.TooltipContent>
                                        想让AI扮演特定角色（如客
                                        <br />
                                        服、翻译、编剧）时使用。
                                    </SC.TooltipContent>
                                }
                            >
                                <SC.Icon type="Question" size={14} />
                            </Tooltip>
                        </SC.Text>
                        <SC.FormItemWrapper>
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                placeholder="在这里写你的提示语"
                                autoHeight
                                value={value.systemPrompt}
                                upstreamOptions={upstreamOptions}
                                onBlur={systemPrompt => onChange({ ...value, systemPrompt })}
                                userOption={userOption}
                                dataSourceList={dataSourceList}
                            />
                        </SC.FormItemWrapper>
                    </SC.Item>

                    <SC.Item>
                        <SC.Text>
                            <Text>用户提示词</Text>
                            <SC.Text color="red">*</SC.Text>
                            <Tooltip
                                title={
                                    <SC.TooltipContent>
                                        向模型提供指令、查询或任
                                        <br />
                                        何基于文本的输入。
                                        <br />
                                        需要精确答案时，需清晰描
                                        <br />
                                        述问题（如“用Python写一
                                        <br />
                                        个排序算法”）。
                                    </SC.TooltipContent>
                                }
                            >
                                <SC.Icon type="Question" size={14} />
                            </Tooltip>
                        </SC.Text>
                        <SC.FormItemWrapper>
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                placeholder="在这里写你的提示语"
                                autoHeight
                                value={value.userPrompt}
                                upstreamOptions={upstreamOptions}
                                onBlur={userPrompt => onChange({ ...value, userPrompt })}
                                userOption={userOption}
                                dataSourceList={dataSourceList}
                            />
                        </SC.FormItemWrapper>
                    </SC.Item>
                </Box>
            </Group>

            <OutputVariable nodeType={AINodeType.LLM_ACTION} />
        </SC.Container>
    )
}
